<template>
  <div class="oa">
    <!-- ── Hero: anillos y veredicto ─────────────────────────────── -->
    <section class="oa-hero">
      <div class="oa-hero__stars" aria-hidden="true"></div>
      <div class="oa-hero__inner">
        <div class="oa-hero__texto">
          <div class="oa-eyebrow"><i class="bi bi-circle-half me-1"></i>Oura Lab</div>
          <h2 class="oa-hero__titulo">Tu cuerpo, en perspectiva</h2>
          <p class="oa-hero__frase" v-if="datos">{{ datos.veredicto.frase }}</p>
          <p class="oa-hero__frase" v-else>Analizando tus noches, tus días y tu recuperación…</p>
          <div v-if="datos" class="oa-marcador">
            <span class="oa-marcador__item oa-marcador__item--mejor">
              <i class="bi bi-arrow-up-right"></i><strong>{{ datos.veredicto.mejoras }}</strong> mejoran
            </span>
            <span class="oa-marcador__item oa-marcador__item--peor">
              <i class="bi bi-arrow-down-right"></i><strong>{{ datos.veredicto.empeoras }}</strong> empeoran
            </span>
            <span class="oa-marcador__item">
              <i class="bi bi-calendar-check"></i><strong>{{ datos.actual.diasConDatos }}</strong>/{{ datos.actual.diasTotales }} días con datos
            </span>
          </div>
          <router-link to="/salud-oura" class="oa-hero__link"><i class="bi bi-arrow-left-short"></i>Datos y sincronización</router-link>
        </div>

        <div class="oa-rings" role="img" :aria-label="ringsAria">
          <div class="oa-rings__dial">
          <svg viewBox="0 0 220 220" class="oa-rings__svg">
            <g v-for="(r, i) in anillos" :key="r.key" transform="rotate(-90 110 110)">
              <circle cx="110" cy="110" :r="r.radio" class="oa-ring__pista" />
              <circle
                cx="110" cy="110" :r="r.radio"
                class="oa-ring__valor"
                :stroke="r.color"
                :stroke-dasharray="circ(r.radio)"
                :stroke-dashoffset="animado ? circ(r.radio) * (1 - r.pct) : circ(r.radio)"
                :style="{ transitionDelay: i * 140 + 'ms' }"
              />
              <line
                v-if="r.pctPrevio != null"
                :x1="110 + (r.radio - 9) * Math.cos(r.pctPrevio * 2 * Math.PI)"
                :y1="110 + (r.radio - 9) * Math.sin(r.pctPrevio * 2 * Math.PI)"
                :x2="110 + (r.radio + 9) * Math.cos(r.pctPrevio * 2 * Math.PI)"
                :y2="110 + (r.radio + 9) * Math.sin(r.pctPrevio * 2 * Math.PI)"
                class="oa-ring__previo"
              />
            </g>
          </svg>
          <div class="oa-rings__centro">
            <div class="oa-rings__indice cifra">{{ indiceAnimado != null ? Math.round(indiceAnimado) : '—' }}</div>
            <div class="oa-rings__sub">Índice Oura</div>
            <div v-if="deltaIndice != null" class="oa-rings__delta" :class="deltaIndice >= 0 ? 'is-up' : 'is-down'">
              <i :class="deltaIndice >= 0 ? 'bi bi-caret-up-fill' : 'bi bi-caret-down-fill'"></i>
              {{ deltaIndice > 0 ? '+' : '' }}{{ deltaIndice.toFixed(1) }}
            </div>
          </div>
          </div>
          <ul class="oa-rings__leyenda">
            <li v-for="r in anillos" :key="r.key">
              <span class="oa-dot" :style="{ background: r.color }"></span>
              {{ r.label }} <strong class="cifra">{{ r.valor != null ? Math.round(r.valor) : '—' }}</strong>
              <small v-if="r.previo != null">vs {{ Math.round(r.previo) }}</small>
            </li>
            <li class="oa-rings__nota"><span class="oa-tick"></span>marca = periodo de referencia</li>
          </ul>
        </div>
      </div>
    </section>

    <!-- ── Filtros ──────────────────────────────────────────────── -->
    <section class="oa-filtros">
      <div class="oa-chips" role="group" aria-label="Rango de fechas">
        <button
          v-for="p in presets"
          :key="p.id"
          type="button"
          class="oa-chip"
          :class="{ 'is-on': preset === p.id }"
          @click="elegirPreset(p.id)"
        >{{ p.label }}</button>
      </div>
      <div v-if="preset === 'custom'" class="oa-fechas">
        <input v-model="desde" type="date" class="form-control form-control-sm" :max="hasta" aria-label="Desde" />
        <span>→</span>
        <input v-model="hasta" type="date" class="form-control form-control-sm" :min="desde" :max="hoy" aria-label="Hasta" />
        <button type="button" class="btn btn-sm btn-primary" @click="cargar">Aplicar</button>
      </div>
      <div class="oa-seg" role="group" aria-label="Comparar con">
        <button type="button" :class="{ 'is-on': comparar === 'anterior' }" @click="cambiarComparar('anterior')">
          <i class="bi bi-skip-backward me-1"></i>Periodo anterior
        </button>
        <button type="button" :class="{ 'is-on': comparar === 'anio' }" @click="cambiarComparar('anio')">
          <i class="bi bi-calendar2-range me-1"></i>Año pasado
        </button>
      </div>
      <div v-if="datos" class="oa-rango">
        <strong>{{ fmtRango(datos.periodo.desde, datos.periodo.hasta) }}</strong>
        <span class="text-muted"> frente a </span>
        <span class="oa-rango__ref">{{ fmtRango(datos.referencia.desde, datos.referencia.hasta) }}</span>
      </div>
    </section>

    <div v-if="error" class="alert alert-danger mx-3">{{ error }}</div>

    <div v-if="cargando && !datos" class="oa-skeleton">
      <div v-for="n in 6" :key="n" class="oa-skeleton__bloque"></div>
    </div>

    <div v-else-if="datos && !datos.actual.diasConDatos" class="oa-vacio">
      <i class="bi bi-moon"></i>
      <h5>Sin datos de Oura en este rango</h5>
      <p>Prueba otro periodo o importa el histórico desde <router-link to="/salud-oura">Salud → Oura</router-link>.</p>
    </div>

    <template v-else-if="datos">
      <!-- ── Descubrimientos ─────────────────────────────────────── -->
      <section v-if="datos.descubrimientos.length" class="oa-seccion">
        <header class="oa-seccion__head">
          <h5><i class="bi bi-lightbulb"></i>Descubrimientos</h5>
          <small>Patrones detectados automáticamente en tus datos</small>
        </header>
        <div class="oa-insights">
          <button
            v-for="(d, i) in datos.descubrimientos"
            :key="i"
            type="button"
            class="oa-insight"
            :class="'oa-insight--' + d.tipo"
            :style="{ animationDelay: i * 60 + 'ms' }"
            @click="abrirDescubrimiento(d)"
          >
            <i :class="'bi ' + d.icono" class="oa-insight__icono"></i>
            <div>
              <div class="oa-insight__titulo">{{ d.titulo }}</div>
              <div class="oa-insight__texto">{{ d.texto }}</div>
            </div>
          </button>
        </div>
      </section>

      <!-- ── Constantes (tiles) ──────────────────────────────────── -->
      <section class="oa-seccion">
        <header class="oa-seccion__head">
          <h5><i class="bi bi-heart-pulse"></i>Constantes</h5>
          <small>Media del periodo · tendencia diaria (línea) frente a referencia (discontinua). Toca una para explorarla.</small>
        </header>
        <div v-for="g in gruposTiles" :key="g.id" class="oa-grupo">
          <div class="oa-grupo__titulo">{{ g.label }}</div>
          <div class="oa-tiles">
            <button
              v-for="m in g.metricas"
              :key="m.key"
              type="button"
              class="oa-tile"
              :class="{ 'is-sel': metricaSel === m.key }"
              @click="seleccionarMetrica(m.key)"
            >
              <div class="oa-tile__label">{{ m.label }}</div>
              <div class="oa-tile__valor cifra">{{ fmt(m.key, mediaDe(m.key)) }}</div>
              <div class="oa-tile__pie">
                <span v-if="datos.comparativa[m.key]" class="oa-delta" :class="'oa-delta--' + datos.comparativa[m.key].tono">
                  <i :class="iconoDelta(m.key)"></i>{{ fmtDelta(m.key) }}
                </span>
                <span v-else class="oa-delta oa-delta--igual">sin referencia</span>
              </div>
              <svg class="oa-spark" viewBox="0 0 100 28" preserveAspectRatio="none" aria-hidden="true">
                <path :d="spark(m.key, true)" class="oa-spark__previo" />
                <path :d="spark(m.key, false)" class="oa-spark__actual" :stroke="colorMetrica(m.key)" />
              </svg>
            </button>
          </div>
        </div>
      </section>

      <!-- ── Duelo de periodos ───────────────────────────────────── -->
      <section class="oa-seccion" ref="seccionDuelo">
        <header class="oa-seccion__head oa-seccion__head--fila">
          <div>
            <h5><i class="bi bi-intersect"></i>Duelo de periodos · {{ metaSel.label }}</h5>
            <small>Día a día, alineados desde el primer día de cada periodo</small>
          </div>
          <select v-model="metricaSel" class="form-select form-select-sm oa-select" aria-label="Métrica">
            <option v-for="m in datos.metricas" :key="m.key" :value="m.key">{{ m.label }}</option>
          </select>
        </header>
        <div class="oa-duelo-leyenda">
          <span><span class="oa-linea" :style="{ background: colorMetrica(metricaSel) }"></span>{{ fmtRango(datos.periodo.desde, datos.periodo.hasta) }} · media {{ fmt(metricaSel, mediaDe(metricaSel)) }}</span>
          <span><span class="oa-linea oa-linea--dash"></span>{{ fmtRango(datos.referencia.desde, datos.referencia.hasta) }} · media {{ fmt(metricaSel, mediaDe(metricaSel, true)) }}</span>
          <span v-if="resaltado" class="oa-resaltado-chip">
            <i class="bi bi-stars"></i>{{ resaltado.motivo }} ({{ resaltado.dias.length }})
            <button type="button" aria-label="Quitar resaltado" @click="resaltado = null"><i class="bi bi-x"></i></button>
          </span>
        </div>
        <div class="oa-chart">
          <Line :data="chartData" :options="chartOptions" />
        </div>
      </section>

      <div class="oa-dos">
        <!-- ── Mapa de calor ─────────────────────────────────────── -->
        <section class="oa-seccion" ref="seccionCalor">
          <header class="oa-seccion__head">
            <h5><i class="bi bi-grid-3x3"></i>Mapa de calor · {{ metaSel.label }}</h5>
            <small>Más intenso = mejor. Toca un día para ver el detalle.</small>
          </header>
          <div v-if="resaltado" class="oa-resaltado-chip mb-2">
            <i class="bi bi-stars"></i>Coach: {{ resaltado.motivo }}
            <button type="button" aria-label="Quitar resaltado" @click="resaltado = null"><i class="bi bi-x"></i></button>
          </div>
          <div class="oa-heat-wrap" :style="{ '--cell': celda + 'px' }">
            <div class="oa-heat-dias" aria-hidden="true">
              <span>L</span><span></span><span>X</span><span></span><span>V</span><span></span><span>D</span>
            </div>
            <div class="oa-heat">
              <button
                v-for="c in celdasCalor"
                :key="c.key"
                type="button"
                class="oa-heat__celda"
                :class="{ 'is-vacia': c.vacia, 'is-hl': c.hl, 'is-sel': diaSel && diaSel.dia === c.dia }"
                :style="{ background: c.color }"
                :title="c.title"
                :disabled="c.vacia"
                @click="abrirDia(c.dia)"
              ></button>
            </div>
          </div>
          <div class="oa-heat-escala">
            <span>peor</span>
            <span v-for="t in [0.15, 0.35, 0.55, 0.75, 1]" :key="t" class="oa-heat-escala__paso" :style="{ background: rgba(colorMetrica(metricaSel), t) }"></span>
            <span>mejor</span>
          </div>

          <transition name="oa-fade">
            <div v-if="diaSel" class="oa-dia">
              <div class="oa-dia__head">
                <strong>{{ fmtDiaLargo(diaSel.dia) }}</strong>
                <button type="button" class="btn-close btn-sm" aria-label="Cerrar" @click="diaSel = null"></button>
              </div>
              <div class="oa-dia__grid">
                <div v-for="m in metricasDia" :key="m.key">
                  <span>{{ m.label }}</span><strong class="cifra">{{ fmt(m.key, diaSel[m.key]) }}</strong>
                </div>
              </div>
              <div v-if="diaSel.workouts.length" class="oa-dia__entrenos">
                <span v-for="(w, i) in diaSel.workouts" :key="i" class="oa-pill">
                  <i class="bi bi-lightning-charge"></i>{{ w.actividad }}<template v-if="w.min"> · {{ w.min }} min</template>
                </span>
              </div>
              <button type="button" class="btn btn-sm btn-outline-primary mt-2" @click="preguntarDia(diaSel.dia)">
                <i class="bi bi-stars me-1"></i>Pregúntale al coach por este día
              </button>
            </div>
          </transition>
        </section>

        <!-- ── Reloj del sueño ───────────────────────────────────── -->
        <section class="oa-seccion">
          <header class="oa-seccion__head">
            <h5><i class="bi bi-clock"></i>Reloj del sueño</h5>
            <small>Ventana media en cama · puntos = cada noche al acostarte</small>
          </header>
          <div class="oa-reloj">
            <svg viewBox="0 0 240 240" class="oa-reloj__svg" role="img" :aria-label="relojAria">
              <circle cx="120" cy="120" r="104" class="oa-reloj__esfera" />
              <g v-for="h in 24" :key="h">
                <line
                  :x1="120 + 104 * Math.sin(((h - 1) / 24) * 2 * Math.PI)"
                  :y1="120 - 104 * Math.cos(((h - 1) / 24) * 2 * Math.PI)"
                  :x2="120 + ((h - 1) % 3 === 0 ? 94 : 99) * Math.sin(((h - 1) / 24) * 2 * Math.PI)"
                  :y2="120 - ((h - 1) % 3 === 0 ? 94 : 99) * Math.cos(((h - 1) / 24) * 2 * Math.PI)"
                  class="oa-reloj__marca"
                />
                <text
                  v-if="(h - 1) % 3 === 0"
                  :x="120 + 82 * Math.sin(((h - 1) / 24) * 2 * Math.PI)"
                  :y="124 - 82 * Math.cos(((h - 1) / 24) * 2 * Math.PI)"
                  class="oa-reloj__hora"
                >{{ String(h - 1).padStart(2, '0') }}</text>
              </g>
              <path v-if="arcoPrevio" :d="arcoPrevio" class="oa-reloj__arco-previo" />
              <path v-if="arcoActual" :d="arcoActual" class="oa-reloj__arco" />
              <circle
                v-for="p in puntosCama"
                :key="p.dia"
                :cx="p.x" :cy="p.y" r="3"
                class="oa-reloj__punto"
                :class="{ 'is-hl': p.hl }"
              ><title>{{ p.title }}</title></circle>
              <text x="120" y="114" class="oa-reloj__centro cifra">{{ fmt('sleepHours', mediaDe('sleepHours')) }}</text>
              <text x="120" y="134" class="oa-reloj__centro-sub">
                {{ fmt('bedtime', mediaDe('bedtime')) }} → {{ fmt('wake', mediaDe('wake')) }}
              </text>
            </svg>
            <ul class="oa-reloj__leyenda">
              <li><span class="oa-linea oa-linea--gruesa"></span>Este periodo</li>
              <li><span class="oa-linea oa-linea--dash"></span>Referencia {{ fmt('bedtime', mediaDe('bedtime', true)) }} → {{ fmt('wake', mediaDe('wake', true)) }}</li>
            </ul>
          </div>

          <div class="oa-arquitectura">
            <div class="oa-arquitectura__titulo">Arquitectura del sueño</div>
            <div v-for="fila in arquitectura" :key="fila.id" class="oa-arq-fila">
              <span class="oa-arq-fila__label">{{ fila.label }}</span>
              <div class="oa-arq-barra">
                <div
                  v-for="s in fila.segmentos"
                  :key="s.key"
                  class="oa-arq-seg"
                  :class="'oa-arq-seg--' + s.key"
                  :style="{ flexGrow: s.min || 0 }"
                  :title="`${s.label}: ${Math.round(s.min || 0)} min`"
                >
                  <span v-if="s.pct >= 14">{{ Math.round(s.min) }}′</span>
                </div>
              </div>
            </div>
            <div class="oa-arq-leyenda">
              <span><i class="oa-arq-seg--deepMin"></i>Profundo</span>
              <span><i class="oa-arq-seg--remMin"></i>REM</span>
              <span><i class="oa-arq-seg--lightMin"></i>Ligero</span>
            </div>
          </div>
        </section>
      </div>

      <div class="oa-dos">
        <!-- ── Huella semanal ────────────────────────────────────── -->
        <section class="oa-seccion">
          <header class="oa-seccion__head oa-seccion__head--fila">
            <div>
              <h5><i class="bi bi-calendar-week"></i>Huella semanal</h5>
              <small>Media por día de la semana</small>
            </div>
            <div class="oa-seg oa-seg--mini">
              <button
                v-for="k in ['readiness', 'sleepScore', 'hrv', 'steps']"
                :key="k"
                type="button"
                :class="{ 'is-on': semanalKey === k }"
                @click="semanalKey = k"
              >{{ labelCorto(k) }}</button>
            </div>
          </header>
          <div class="oa-semana">
            <div v-for="f in filasSemana" :key="f.nombre" class="oa-semana__fila" :class="{ 'is-top': f.top }">
              <span class="oa-semana__dia">{{ f.nombre.slice(0, 3) }}</span>
              <div class="oa-semana__barras">
                <div class="oa-semana__barra" :style="{ width: f.pct + '%', background: colorMetrica(semanalKey) }"></div>
                <div class="oa-semana__barra oa-semana__barra--previo" :style="{ width: f.pctPrevio + '%' }"></div>
              </div>
              <span class="oa-semana__valor cifra">{{ fmt(semanalKey, f.valor) }}</span>
            </div>
          </div>
          <div class="oa-semana__leyenda">
            <span><i :style="{ background: colorMetrica(semanalKey) }"></i>Este periodo</span>
            <span><i class="is-previo"></i>Referencia</span>
          </div>
        </section>

        <!-- ── Día estrella / Día difícil ────────────────────────── -->
        <section class="oa-seccion">
          <header class="oa-seccion__head">
            <h5><i class="bi bi-star"></i>Extremos del periodo</h5>
            <small>Según la media de readiness, sueño y actividad</small>
          </header>
          <div class="oa-extremos">
            <button
              v-for="e in extremos"
              :key="e.id"
              type="button"
              class="oa-extremo"
              :class="'oa-extremo--' + e.id"
              @click="abrirDia(e.d.dia)"
            >
              <div class="oa-extremo__head">
                <i :class="e.icono"></i>{{ e.label }}
              </div>
              <div class="oa-extremo__dia">{{ fmtDiaLargo(e.d.dia) }}</div>
              <div class="oa-extremo__media cifra">{{ Math.round(e.d.media) }}</div>
              <div class="oa-extremo__datos">
                <span>Readiness <strong>{{ fmt('readiness', e.d.readiness) }}</strong></span>
                <span>Sueño <strong>{{ fmt('sleepHours', e.d.sleepHours) }}</strong></span>
                <span>VFC <strong>{{ fmt('hrv', e.d.hrv) }}</strong></span>
                <span>Pasos <strong>{{ fmt('steps', e.d.steps) }}</strong></span>
              </div>
            </button>
          </div>
          <div class="oa-entrenos-resumen">
            <i class="bi bi-lightning-charge"></i>
            <strong>{{ datos.actual.entrenos.sesiones }}</strong> entrenos · <strong>{{ datos.actual.entrenos.minutos }}</strong> min
            <span class="text-muted">(antes {{ datos.previo.entrenos.sesiones }} · {{ datos.previo.entrenos.minutos }} min)</span>
          </div>
        </section>
      </div>
    </template>

    <!-- ── Coach Oura (agente IA) ──────────────────────────────── -->
    <button
      type="button"
      class="oa-orbe"
      :class="{ 'is-open': chatAbierto }"
      :aria-label="chatAbierto ? 'Cerrar coach' : 'Abrir Coach Oura'"
      @click="toggleChat"
    >
      <span class="oa-orbe__halo"></span>
      <i :class="chatAbierto ? 'bi bi-x-lg' : 'bi bi-stars'"></i>
    </button>

    <transition name="oa-sube">
      <aside v-if="chatAbierto" class="oa-coach" aria-label="Coach Oura">
        <header class="oa-coach__head">
          <div class="oa-coach__avatar"><i class="bi bi-stars"></i></div>
          <div class="flex-grow-1">
            <div class="oa-coach__nombre">Coach Oura</div>
            <div class="oa-coach__ctx" v-if="datos">Viendo {{ fmtRango(datos.periodo.desde, datos.periodo.hasta) }} · vs {{ comparar === 'anio' ? 'año pasado' : 'periodo anterior' }}</div>
          </div>
          <button v-if="mensajes.length" type="button" class="btn btn-sm btn-link oa-coach__limpiar" @click="limpiarChat">Limpiar</button>
        </header>
        <div ref="hilo" class="oa-coach__hilo">
          <div v-if="datos && !datos.llmConfigured" class="alert alert-warning small mb-2">
            Configura <code>DEEPSEEK_API_KEY</code> u <code>OPENAI_API_KEY</code> en el servidor para activar el coach.
          </div>
          <div v-if="!mensajes.length" class="oa-coach__intro">
            <p>Pregúntame lo que quieras sobre tus datos. Puedo comparar periodos, buscar causas y <strong>marcar días en tu calendario</strong>.</p>
            <div class="oa-coach__sugerencias">
              <button
                v-for="s in sugerencias"
                :key="s"
                type="button"
                :disabled="enviando || (datos && !datos.llmConfigured)"
                @click="enviar(s)"
              >{{ s }}</button>
            </div>
          </div>
          <div v-for="m in mensajes" :key="m.id" class="oa-msg" :class="'oa-msg--' + m.role">
            <!-- eslint-disable-next-line vue/no-v-html -->
            <div class="oa-msg__burbuja" v-html="formatear(m.content)"></div>
            <button v-if="m.resaltado && m.resaltado.dias.length" type="button" class="oa-msg__accion" @click="aplicarResaltado(m.resaltado)">
              <i class="bi bi-geo-alt"></i>Ver {{ m.resaltado.dias.length }} {{ m.resaltado.dias.length === 1 ? 'día' : 'días' }} en el calendario
            </button>
          </div>
          <div v-if="enviando" class="oa-msg oa-msg--assistant">
            <div class="oa-msg__burbuja oa-pensando"><span></span><span></span><span></span></div>
          </div>
        </div>
        <form class="oa-coach__form" @submit.prevent="enviar()">
          <input
            v-model="texto"
            type="text"
            class="form-control"
            placeholder="¿Por qué ha bajado mi VFC?"
            maxlength="2000"
            :disabled="enviando || (datos && !datos.llmConfigured)"
          />
          <button class="btn btn-primary" :disabled="enviando || !texto.trim() || (datos && !datos.llmConfigured)" aria-label="Enviar">
            <i class="bi bi-send-fill"></i>
          </button>
        </form>
      </aside>
    </transition>
  </div>
</template>

<script>
import axios from '../config/axios';
import { Line } from 'vue-chartjs';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  Tooltip,
  LineElement,
  PointElement,
  LineController,
  Filler,
} from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, Tooltip, LineElement, PointElement, LineController, Filler);

// Paleta categórica validada (CVD) para fondo claro y su variante para el hero oscuro.
const COLOR = { readiness: '#14895a', sleep: '#4f5bd5', activity: '#d9622b' };
const COLOR_HERO = { readiness: '#26a86b', sleep: '#7781f5', activity: '#d96a32' };
const COLOR_PREVIO = '#9aa39d';
const COLOR_HL = '#b42318';
const COLOR_GRUPO = { score: COLOR.readiness, cuerpo: COLOR.readiness, sueno: COLOR.sleep, actividad: COLOR.activity };
const MESES = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
const DIAS = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'];
const CHAT_KEY = 'oura-analisis-chat';

function ymdLocal(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function addDays(ymd, n) {
  const d = new Date(`${ymd}T12:00:00`);
  d.setDate(d.getDate() + n);
  return ymdLocal(d);
}
function minutosAHora(min) {
  if (min == null) return '—';
  const t = (((Math.round(min) + 720) % 1440) + 1440) % 1440;
  return `${String(Math.floor(t / 60)).padStart(2, '0')}:${String(t % 60).padStart(2, '0')}`;
}
function hexRgb(hex) {
  const n = parseInt(hex.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}
function escapar(s) {
  return String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}
function leerChat() {
  try {
    return JSON.parse(sessionStorage.getItem(CHAT_KEY) || '[]');
  } catch {
    return [];
  }
}

export default {
  name: 'OuraAnalisis',
  components: { Line },
  data() {
    const hoy = ymdLocal(new Date());
    return {
      hoy,
      preset: '30',
      desde: addDays(hoy, -29),
      hasta: hoy,
      comparar: 'anterior',
      datos: null,
      cargando: false,
      error: null,
      animado: false,
      indiceAnimado: null,
      metricaSel: 'readiness',
      semanalKey: 'readiness',
      diaSel: null,
      resaltado: null,
      chatAbierto: false,
      mensajes: leerChat(),
      texto: '',
      enviando: false,
      celda: 15,
      presets: [
        { id: '7', label: '7 días' },
        { id: '30', label: '30 días' },
        { id: '90', label: '90 días' },
        { id: 'mes', label: 'Este mes' },
        { id: 'mesPasado', label: 'Mes pasado' },
        { id: 'anio', label: 'Este año' },
        { id: '365', label: '12 meses' },
        { id: 'custom', label: 'Personalizado' },
      ],
    };
  },
  computed: {
    meta() {
      return Object.fromEntries((this.datos?.metricas || []).map((m) => [m.key, m]));
    },
    metaSel() {
      return this.meta[this.metricaSel] || { label: '' };
    },
    anillos() {
      const defs = [
        { key: 'readiness', label: 'Readiness', radio: 92, color: COLOR_HERO.readiness },
        { key: 'sleepScore', label: 'Sueño', radio: 74, color: COLOR_HERO.sleep },
        { key: 'activityScore', label: 'Actividad', radio: 56, color: COLOR_HERO.activity },
      ];
      return defs.map((d) => {
        const valor = this.mediaDe(d.key);
        const previo = this.mediaDe(d.key, true);
        return {
          ...d,
          valor,
          previo,
          pct: valor != null ? Math.min(valor / 100, 1) : 0,
          pctPrevio: previo != null ? Math.min(previo / 100, 0.999) : null,
        };
      });
    },
    ringsAria() {
      return this.anillos.map((r) => `${r.label} ${r.valor != null ? Math.round(r.valor) : 'sin datos'}`).join(', ');
    },
    deltaIndice() {
      const v = this.datos?.veredicto;
      return v && v.indice != null && v.indicePrevio != null ? v.indice - v.indicePrevio : null;
    },
    gruposTiles() {
      if (!this.datos) return [];
      const g = [
        { id: 'cuerpo', label: 'Cuerpo y recuperación' },
        { id: 'sueno', label: 'Sueño' },
        { id: 'actividad', label: 'Actividad' },
      ];
      return g
        .map((x) => ({ ...x, metricas: this.datos.metricas.filter((m) => m.grupo === x.id && this.datos.actual.metricas[m.key]) }))
        .filter((x) => x.metricas.length);
    },
    prevDates() {
      return (this.datos?.seriePrevia || []).map((d) => d.dia);
    },
    hlSet() {
      return new Set(this.resaltado ? this.resaltado.dias : []);
    },
    chartData() {
      if (!this.datos) return { labels: [], datasets: [] };
      const k = this.metricaSel;
      const color = this.colorMetrica(k);
      const serie = this.datos.serie;
      const hl = serie.map((d) => this.hlSet.has(d.dia));
      return {
        labels: serie.map((d) => this.fmtCorto(d.dia)),
        datasets: [
          {
            label: 'Este periodo',
            data: serie.map((d) => d[k]),
            borderColor: color,
            borderWidth: 2,
            tension: 0.35,
            spanGaps: true,
            fill: 'origin',
            backgroundColor: (ctx) => {
              const { chart } = ctx;
              if (!chart.chartArea) return this.rgba(color, 0.08);
              const g = chart.ctx.createLinearGradient(0, chart.chartArea.top, 0, chart.chartArea.bottom);
              g.addColorStop(0, this.rgba(color, 0.22));
              g.addColorStop(1, this.rgba(color, 0));
              return g;
            },
            pointRadius: hl.map((h) => (h ? 6 : 0)),
            pointHoverRadius: 5,
            pointBackgroundColor: hl.map((h) => (h ? COLOR_HL : color)),
            pointBorderColor: '#fbfcf9',
            pointBorderWidth: 2,
          },
          {
            label: 'Referencia',
            data: this.datos.seriePrevia.map((d) => d[k]),
            borderColor: COLOR_PREVIO,
            borderDash: [5, 4],
            borderWidth: 2,
            tension: 0.35,
            spanGaps: true,
            pointRadius: 0,
            pointHoverRadius: 4,
            fill: false,
          },
        ],
      };
    },
    chartOptions() {
      const k = this.metricaSel;
      const esHora = k === 'bedtime' || k === 'wake';
      return {
        responsive: true,
        maintainAspectRatio: false,
        animation: { duration: 500 },
        interaction: { mode: 'index', intersect: false },
        plugins: {
          legend: { display: false },
          tooltip: {
            backgroundColor: '#163528',
            padding: 10,
            callbacks: {
              title: (items) => (items.length ? this.fmtDiaLargo(this.datos.serie[items[0].dataIndex].dia) : ''),
              label: (item) => {
                const val = this.fmt(k, item.raw);
                if (item.datasetIndex === 1) return ` Ref. (${this.fmtCorto(this.prevDates[item.dataIndex])}): ${val}`;
                return ` ${val}`;
              },
            },
          },
        },
        scales: {
          x: { grid: { display: false }, ticks: { maxTicksLimit: 8, color: '#5c675e', maxRotation: 0 } },
          y: {
            grid: { color: 'rgba(22,53,40,0.07)' },
            border: { display: false },
            ticks: { color: '#5c675e', maxTicksLimit: 5, callback: (v) => (esHora ? minutosAHora(v) : this.fmtEje(k, v)) },
          },
        },
      };
    },
    celdasCalor() {
      if (!this.datos) return [];
      const k = this.metricaSel;
      const m = this.meta[k];
      const serie = this.datos.serie;
      const vals = serie.map((d) => d[k]).filter((v) => v != null);
      const min = Math.min(...vals);
      const max = Math.max(...vals);
      const color = this.colorMetrica(k);
      const pad = (new Date(`${serie[0].dia}T12:00:00`).getDay() + 6) % 7;
      const out = [];
      for (let i = 0; i < pad; i++) out.push({ key: `p${i}`, vacia: true, color: 'transparent' });
      for (const d of serie) {
        const v = d[k];
        let t = null;
        if (v != null) {
          t = max > min ? (v - min) / (max - min) : 1;
          if (m && m.better === 'down') t = 1 - t;
        }
        out.push({
          key: d.dia,
          dia: d.dia,
          vacia: v == null,
          hl: this.hlSet.has(d.dia),
          color: v == null ? 'var(--oa-vacio)' : this.rgba(color, 0.12 + 0.88 * t),
          title: `${this.fmtDiaLargo(d.dia)} · ${this.fmt(k, v)}`,
        });
      }
      return out;
    },
    metricasDia() {
      const keys = ['readiness', 'sleepScore', 'activityScore', 'hrv', 'restingHr', 'sleepHours', 'deepMin', 'remMin', 'bedtime', 'steps', 'activeCalories', 'tempDev'];
      return keys.map((k) => this.meta[k]).filter(Boolean);
    },
    arcoActual() {
      return this.arco(this.mediaDe('bedtime'), this.mediaDe('wake'), 92);
    },
    arcoPrevio() {
      return this.arco(this.mediaDe('bedtime', true), this.mediaDe('wake', true), 112);
    },
    puntosCama() {
      if (!this.datos) return [];
      return this.datos.serie
        .filter((d) => d.bedtime != null)
        .map((d) => {
          const a = this.angulo(d.bedtime);
          return {
            dia: d.dia,
            x: 120 + 70 * Math.sin(a),
            y: 120 - 70 * Math.cos(a),
            hl: this.hlSet.has(d.dia),
            title: `${this.fmtCorto(d.dia)} · ${minutosAHora(d.bedtime)}`,
          };
        });
    },
    relojAria() {
      return `Te acuestas de media a las ${this.fmt('bedtime', this.mediaDe('bedtime'))} y te levantas a las ${this.fmt('wake', this.mediaDe('wake'))}`;
    },
    arquitectura() {
      const fila = (id, label, previo) => {
        const segs = [
          { key: 'deepMin', label: 'Profundo' },
          { key: 'remMin', label: 'REM' },
          { key: 'lightMin', label: 'Ligero' },
        ].map((s) => ({ ...s, min: this.mediaDe(s.key, previo) }));
        const total = segs.reduce((a, s) => a + (s.min || 0), 0) || 1;
        return { id, label, segmentos: segs.map((s) => ({ ...s, pct: ((s.min || 0) / total) * 100 })) };
      };
      return [fila('a', 'Ahora', false), fila('b', 'Antes', true)];
    },
    filasSemana() {
      if (!this.datos) return [];
      const k = this.semanalKey;
      const orden = [1, 2, 3, 4, 5, 6, 0];
      const act = this.datos.semanal.actual;
      const pre = this.datos.semanal.previo;
      const todos = [...act, ...pre].map((s) => s[k]).filter((v) => v != null);
      const max = Math.max(...todos, 1);
      const min = k === 'steps' ? 0 : Math.max(0, Math.min(...todos) * 0.8);
      const escala = (v) => (v == null ? 0 : Math.max(4, ((v - min) / (max - min || 1)) * 100));
      const valsAct = act.map((s) => s[k]).filter((v) => v != null);
      const top = valsAct.length ? Math.max(...valsAct) : null;
      return orden.map((wd) => {
        const a = act.find((s) => s.wd === wd);
        const p = pre.find((s) => s.wd === wd);
        return { nombre: a.nombre, valor: a[k], pct: escala(a[k]), pctPrevio: escala(p && p[k]), top: a[k] != null && a[k] === top };
      });
    },
    extremos() {
      const e = this.datos?.extremos;
      if (!e) return [];
      return [
        e.mejor && { id: 'mejor', label: 'Día estrella', icono: 'bi bi-star-fill', d: e.mejor },
        e.peor && e.peor.dia !== e.mejor?.dia && { id: 'peor', label: 'Día difícil', icono: 'bi bi-cloud-drizzle', d: e.peor },
      ].filter(Boolean);
    },
    sugerencias() {
      const out = [];
      const c = this.datos?.comparativa || {};
      const peor = Object.entries(c).filter(([, v]) => v && v.tono === 'peor').sort((a, b) => Math.abs(b[1].pct || 0) - Math.abs(a[1].pct || 0))[0];
      if (peor && this.meta[peor[0]]) out.push(`¿Por qué ha empeorado mi ${this.meta[peor[0]].label.toLowerCase()}?`);
      out.push('Resume este periodo frente al anterior');
      out.push('¿Cuáles fueron mis 3 peores noches y qué tenían en común?');
      out.push('¿Cómo afectan mis entrenos a la VFC del día siguiente?');
      out.push('Dame un plan para subir mi readiness la próxima semana');
      return out.slice(0, 5);
    },
  },
  watch: {
    mensajes: {
      deep: true,
      handler(v) {
        try {
          sessionStorage.setItem(CHAT_KEY, JSON.stringify(v.slice(-30)));
        } catch {
          /* almacenamiento no disponible */
        }
      },
    },
  },
  mounted() {
    this.ajustarCelda();
    window.addEventListener('resize', this.ajustarCelda);
    this.cargar();
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.ajustarCelda);
    cancelAnimationFrame(this._raf);
  },
  methods: {
    rgba(hex, a) {
      const [r, g, b] = hexRgb(hex);
      return `rgba(${r},${g},${b},${a})`;
    },
    circ(r) {
      return 2 * Math.PI * r;
    },
    colorMetrica(k) {
      if (k === 'readiness') return COLOR.readiness;
      if (k === 'sleepScore') return COLOR.sleep;
      if (k === 'activityScore') return COLOR.activity;
      return COLOR_GRUPO[this.meta[k]?.grupo] || COLOR.readiness;
    },
    labelCorto(k) {
      return { readiness: 'Readiness', sleepScore: 'Sueño', hrv: 'VFC', steps: 'Pasos' }[k] || k;
    },
    mediaDe(k, previo = false) {
      const r = this.datos?.[previo ? 'previo' : 'actual']?.metricas?.[k];
      return r ? r.media : null;
    },
    fmt(k, v) {
      if (v == null || Number.isNaN(v)) return '—';
      const m = this.meta[k] || { dec: 0, unidad: '' };
      if (k === 'bedtime' || k === 'wake') return minutosAHora(v);
      if (k === 'sleepHours') {
        const h = Math.floor(v);
        return `${h}h ${String(Math.round((v - h) * 60)).padStart(2, '0')}m`;
      }
      if (k === 'steps') return Math.round(v).toLocaleString('es-ES');
      if (k === 'tempDev') return `${v > 0 ? '+' : ''}${v.toFixed(2).replace('.', ',')} °C`;
      const num = Number(v).toLocaleString('es-ES', { maximumFractionDigits: m.dec, minimumFractionDigits: m.dec });
      return m.unidad === 'pts' ? num : `${num} ${m.unidad}`;
    },
    fmtEje(k, v) {
      if (k === 'steps') return v >= 1000 ? `${Math.round(v / 100) / 10}k` : v;
      if (k === 'sleepHours') return `${v}h`;
      return Number(v).toLocaleString('es-ES', { maximumFractionDigits: 2 });
    },
    fmtDelta(k) {
      const c = this.datos.comparativa[k];
      if (!c) return '';
      if (k === 'bedtime' || k === 'wake') {
        const min = Math.round(c.delta);
        if (!min) return 'igual';
        return `${Math.abs(min)} min ${min > 0 ? 'más tarde' : 'antes'}`;
      }
      if (k === 'tempDev') return `${c.delta > 0 ? '+' : ''}${c.delta.toFixed(2).replace('.', ',')} °C`;
      if (k === 'sleepHours') {
        const min = Math.round(c.delta * 60);
        return `${min > 0 ? '+' : ''}${min} min`;
      }
      if (c.pct == null) return `${c.delta > 0 ? '+' : ''}${c.delta}`;
      return `${c.pct > 0 ? '+' : ''}${c.pct.toLocaleString('es-ES')}%`;
    },
    iconoDelta(k) {
      const c = this.datos.comparativa[k];
      if (!c || Math.abs(c.delta) < 1e-9) return 'bi bi-dash';
      return c.delta > 0 ? 'bi bi-arrow-up-short' : 'bi bi-arrow-down-short';
    },
    spark(k, previo) {
      const s = this.datos[previo ? 'seriePrevia' : 'serie'];
      const all = [...this.datos.serie, ...this.datos.seriePrevia].map((d) => d[k]).filter((v) => v != null);
      if (!all.length) return '';
      const min = Math.min(...all);
      const max = Math.max(...all);
      const n = Math.max(s.length - 1, 1);
      let d = '';
      let pen = false;
      s.forEach((p, i) => {
        if (p[k] == null) {
          pen = false;
          return;
        }
        const x = (i / n) * 100;
        const y = 25 - (max > min ? ((p[k] - min) / (max - min)) * 22 : 11);
        d += `${pen ? 'L' : 'M'}${x.toFixed(1)},${y.toFixed(1)} `;
        pen = true;
      });
      return d;
    },
    angulo(min) {
      const t = (((min + 720) % 1440) + 1440) % 1440;
      return (t / 1440) * 2 * Math.PI;
    },
    arco(ini, fin, r) {
      if (ini == null || fin == null) return '';
      const a0 = this.angulo(ini);
      let a1 = this.angulo(fin);
      if (a1 <= a0) a1 += 2 * Math.PI;
      const p = (a) => `${(120 + r * Math.sin(a)).toFixed(2)},${(120 - r * Math.cos(a)).toFixed(2)}`;
      const large = a1 - a0 > Math.PI ? 1 : 0;
      return `M${p(a0)} A${r},${r} 0 ${large} 1 ${p(a1)}`;
    },
    fmtCorto(ymd) {
      if (!ymd) return '';
      const [, m, d] = ymd.split('-').map(Number);
      return `${d} ${MESES[m - 1]}`;
    },
    fmtDiaLargo(ymd) {
      const dt = new Date(`${ymd}T12:00:00`);
      return `${DIAS[dt.getDay()]} ${dt.getDate()} ${MESES[dt.getMonth()]} ${dt.getFullYear()}`;
    },
    fmtRango(a, b) {
      const ya = a.slice(0, 4);
      const yb = b.slice(0, 4);
      return ya === yb ? `${this.fmtCorto(a)} – ${this.fmtCorto(b)} ${yb}` : `${this.fmtCorto(a)} ${ya} – ${this.fmtCorto(b)} ${yb}`;
    },
    ajustarCelda() {
      const n = this.datos ? this.datos.serie.length : 30;
      const semanas = Math.ceil(n / 7) + 1;
      const ancho = Math.min(window.innerWidth - 90, 560);
      this.celda = Math.max(9, Math.min(34, Math.floor(ancho / semanas) - 3));
    },
    elegirPreset(id) {
      this.preset = id;
      const hoy = this.hoy;
      const d = new Date(`${hoy}T12:00:00`);
      if (id === 'custom') return;
      if (['7', '30', '90', '365'].includes(id)) {
        this.desde = addDays(hoy, -(Number(id) - 1));
        this.hasta = hoy;
      } else if (id === 'mes') {
        this.desde = `${hoy.slice(0, 8)}01`;
        this.hasta = hoy;
      } else if (id === 'mesPasado') {
        const ini = new Date(d.getFullYear(), d.getMonth() - 1, 1);
        const fin = new Date(d.getFullYear(), d.getMonth(), 0);
        this.desde = ymdLocal(ini);
        this.hasta = ymdLocal(fin);
      } else if (id === 'anio') {
        this.desde = `${hoy.slice(0, 4)}-01-01`;
        this.hasta = hoy;
      }
      this.cargar();
    },
    cambiarComparar(m) {
      if (this.comparar === m) return;
      this.comparar = m;
      this.cargar();
    },
    async cargar() {
      if (!this.desde || !this.hasta || this.desde > this.hasta) {
        this.error = 'Revisa el rango de fechas.';
        return;
      }
      this.cargando = true;
      this.error = null;
      try {
        const { data } = await axios.get('/api/oura/analisis', {
          params: { desde: this.desde, hasta: this.hasta, comparar: this.comparar },
          timeout: 30000,
        });
        this.datos = data;
        this.diaSel = null;
        this.ajustarCelda();
        this.animar();
      } catch (e) {
        this.error = e.response?.data?.error || e.message || 'No se pudo cargar el análisis';
      } finally {
        this.cargando = false;
      }
    },
    animar() {
      this.animado = false;
      const reducir = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const objetivo = this.datos?.veredicto?.indice;
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.animado = true;
        });
      });
      if (objetivo == null || reducir) {
        this.indiceAnimado = objetivo;
        return;
      }
      const ini = performance.now();
      const desde = this.indiceAnimado || 0;
      cancelAnimationFrame(this._raf);
      const paso = (t) => {
        const p = Math.min((t - ini) / 900, 1);
        this.indiceAnimado = desde + (objetivo - desde) * (1 - (1 - p) ** 3);
        if (p < 1) this._raf = requestAnimationFrame(paso);
      };
      this._raf = requestAnimationFrame(paso);
    },
    seleccionarMetrica(k) {
      this.metricaSel = k;
      this.$nextTick(() => this.$refs.seccionDuelo?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
    },
    abrirDia(dia) {
      const d = this.datos?.serie.find((x) => x.dia === dia);
      if (!d) return;
      this.diaSel = d;
      this.$nextTick(() => this.$refs.seccionCalor?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }));
    },
    abrirDescubrimiento(d) {
      if (d.metrica) this.metricaSel = d.metrica;
      if (d.dias && d.dias.length) this.aplicarResaltado({ dias: d.dias, motivo: d.titulo });
      else this.$nextTick(() => this.$refs.seccionDuelo?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
    },
    aplicarResaltado(r) {
      this.resaltado = { dias: r.dias, motivo: r.motivo };
      this.$nextTick(() => this.$refs.seccionCalor?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
    },
    toggleChat() {
      this.chatAbierto = !this.chatAbierto;
      if (this.chatAbierto) this.bajarHilo();
    },
    preguntarDia(dia) {
      this.chatAbierto = true;
      this.enviar(`Analiza mi ${this.fmtDiaLargo(dia)} (${dia}): ¿qué pasó y cómo se compara con mi media?`);
    },
    bajarHilo() {
      this.$nextTick(() => {
        const h = this.$refs.hilo;
        if (h) h.scrollTop = h.scrollHeight;
      });
    },
    async enviar(sugerencia) {
      const mensaje = (sugerencia || this.texto).trim();
      if (!mensaje || this.enviando) return;
      this.texto = '';
      const history = this.mensajes.filter((m) => !m.error).map((m) => ({ role: m.role, content: m.content }));
      this.mensajes.push({ id: `u${Date.now()}`, role: 'user', content: mensaje });
      this.enviando = true;
      this.bajarHilo();
      try {
        const { data } = await axios.post(
          '/api/oura/analisis/chat',
          { message: mensaje, history, desde: this.desde, hasta: this.hasta, comparar: this.comparar },
          { timeout: 120000 }
        );
        this.mensajes.push({ id: `a${Date.now()}`, role: 'assistant', content: data.reply, resaltado: data.resaltado });
        if (data.resaltado && data.resaltado.dias.length) this.resaltado = data.resaltado;
      } catch (e) {
        this.mensajes.push({
          id: `e${Date.now()}`,
          role: 'assistant',
          error: true,
          content: e.response?.data?.error || 'El coach no pudo responder.',
        });
      } finally {
        this.enviando = false;
        this.bajarHilo();
      }
    },
    limpiarChat() {
      this.mensajes = [];
      this.resaltado = null;
    },
    formatear(txt) {
      return escapar(txt || '')
        .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
        .replace(/^#{1,4}\s*(.+)$/gm, '<strong>$1</strong>')
        .replace(/^\s*[-*]\s+/gm, '• ')
        .replace(/\n/g, '<br>');
    },
  },
};
</script>

<style scoped>
.oa {
  --oa-night: #0d1b16;
  --oa-night-2: #16302a;
  --oa-vacio: rgba(22, 53, 40, 0.05);
  --oa-mejor: #1f6b45;
  --oa-peor: #b42318;
  padding-bottom: 6rem;
}

/* Hero */
.oa-hero {
  position: relative;
  overflow: hidden;
  margin: 0 0 1rem;
  color: #eef4ef;
  background:
    radial-gradient(120% 90% at 85% 10%, rgba(119, 129, 245, 0.28), transparent 55%),
    radial-gradient(90% 80% at 10% 100%, rgba(38, 168, 107, 0.25), transparent 60%),
    linear-gradient(160deg, var(--oa-night), var(--oa-night-2));
}
.oa-hero__stars {
  position: absolute;
  inset: 0;
  background-image:
    radial-gradient(1px 1px at 12% 22%, rgba(255, 255, 255, 0.7), transparent),
    radial-gradient(1px 1px at 32% 68%, rgba(255, 255, 255, 0.5), transparent),
    radial-gradient(1.5px 1.5px at 58% 18%, rgba(255, 255, 255, 0.6), transparent),
    radial-gradient(1px 1px at 76% 82%, rgba(255, 255, 255, 0.45), transparent),
    radial-gradient(1px 1px at 90% 40%, rgba(255, 255, 255, 0.55), transparent),
    radial-gradient(1px 1px at 46% 44%, rgba(255, 255, 255, 0.35), transparent);
  animation: oa-titilar 6s ease-in-out infinite alternate;
  pointer-events: none;
}
@keyframes oa-titilar { from { opacity: 0.45; } to { opacity: 1; } }
.oa-hero__inner {
  position: relative;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 1.5rem;
  max-width: 1200px;
  margin: 0 auto;
  padding: 1.75rem 1rem 1.5rem;
}
.oa-hero__texto { flex: 1 1 300px; min-width: 0; }
.oa-eyebrow {
  font-size: 0.75rem;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: #a9c7b6;
}
.oa-hero__titulo {
  font-family: var(--fuente-cifra);
  font-size: clamp(1.6rem, 4vw, 2.4rem);
  margin: 0.25rem 0 0.5rem;
  color: #fff;
}
.oa-hero__frase { font-size: 1.05rem; color: #d3e2d8; margin-bottom: 0.85rem; max-width: 34rem; }
.oa-marcador { display: flex; flex-wrap: wrap; gap: 0.5rem; margin-bottom: 0.85rem; }
.oa-marcador__item {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.3rem 0.65rem;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.12);
  font-size: 0.85rem;
}
.oa-marcador__item--mejor i { color: #6fd3a0; }
.oa-marcador__item--peor i { color: #ff9b8a; }
.oa-hero__link { color: #a9c7b6; font-size: 0.85rem; text-decoration: none; }
.oa-hero__link:hover { color: #fff; }

.oa-rings { position: relative; display: flex; align-items: center; gap: 1rem; flex-wrap: wrap; justify-content: center; }
.oa-rings__dial { position: relative; width: 220px; height: 220px; flex: none; }
.oa-rings__svg { width: 220px; height: 220px; }
.oa-ring__pista { fill: none; stroke: rgba(255, 255, 255, 0.08); stroke-width: 13; }
.oa-ring__valor {
  fill: none;
  stroke-width: 13;
  stroke-linecap: round;
  transition: stroke-dashoffset 1.2s cubic-bezier(0.2, 0.8, 0.2, 1);
  filter: drop-shadow(0 0 6px rgba(255, 255, 255, 0.15));
}
.oa-ring__previo { stroke: #fff; stroke-width: 2.5; stroke-linecap: round; opacity: 0.85; }
.oa-rings__centro {
  position: absolute;
  left: 0;
  top: 0;
  width: 220px;
  height: 220px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}
.oa-rings__indice { font-size: 2.3rem; line-height: 1; color: #fff; }
.oa-rings__sub { font-size: 0.7rem; letter-spacing: 0.1em; text-transform: uppercase; color: #a9c7b6; }
.oa-rings__delta { font-size: 0.8rem; margin-top: 0.15rem; }
.oa-rings__delta.is-up { color: #6fd3a0; }
.oa-rings__delta.is-down { color: #ff9b8a; }
.oa-rings__leyenda { list-style: none; padding: 0; margin: 0; font-size: 0.9rem; }
.oa-rings__leyenda li { display: flex; align-items: center; gap: 0.4rem; margin-bottom: 0.35rem; }
.oa-rings__leyenda small { color: #a9c7b6; }
.oa-rings__nota { font-size: 0.75rem; color: #a9c7b6; }
.oa-dot { width: 10px; height: 10px; border-radius: 50%; display: inline-block; }
.oa-tick { width: 2px; height: 12px; background: #fff; display: inline-block; border-radius: 1px; }

/* Filtros */
.oa-filtros {
  position: sticky;
  top: 0;
  z-index: 20;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.6rem 1rem;
  max-width: 1200px;
  margin: -2rem auto 1rem;
  padding: 0.75rem 1rem;
  background: rgba(251, 252, 249, 0.92);
  backdrop-filter: blur(10px);
  border: 1px solid var(--linea);
  border-radius: 14px;
  box-shadow: 0 8px 24px rgba(13, 27, 22, 0.08);
}
@media (min-width: 1232px) { .oa-filtros { margin-left: auto; margin-right: auto; } }
@media (max-width: 1231px) { .oa-filtros { margin-left: 0.75rem; margin-right: 0.75rem; } }
.oa-chips { display: flex; flex-wrap: wrap; gap: 0.35rem; }
.oa-chip {
  border: 1px solid var(--linea);
  background: var(--superficie);
  color: var(--tinta);
  border-radius: 999px;
  padding: 0.25rem 0.7rem;
  font-size: 0.85rem;
  transition: all 0.15s;
}
.oa-chip:hover { border-color: var(--marca); }
.oa-chip.is-on { background: var(--marca-oscura); color: #fff; border-color: var(--marca-oscura); }
.oa-fechas { display: flex; align-items: center; gap: 0.4rem; }
.oa-fechas input { width: 9.5rem; }
.oa-seg { display: inline-flex; border: 1px solid var(--linea); border-radius: 999px; padding: 2px; background: var(--papel); }
.oa-seg button {
  border: 0;
  background: transparent;
  border-radius: 999px;
  padding: 0.2rem 0.7rem;
  font-size: 0.82rem;
  color: var(--tinta-suave);
}
.oa-seg button.is-on { background: var(--superficie); color: var(--tinta); box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12); }
.oa-seg--mini button { padding: 0.15rem 0.5rem; font-size: 0.75rem; }
.oa-rango { font-size: 0.85rem; margin-left: auto; }
.oa-rango__ref { color: var(--tinta-suave); }

/* Secciones */
.oa-seccion {
  max-width: 1200px;
  margin: 0 auto 1rem;
  padding: 1.1rem 1rem;
  background: var(--superficie);
  border: 1px solid var(--linea);
  border-radius: 16px;
}
@media (max-width: 1231px) { .oa-seccion { margin-left: 0.75rem; margin-right: 0.75rem; } }
.oa-seccion__head { margin-bottom: 0.85rem; }
.oa-seccion__head h5 { font-size: 1.02rem; font-weight: 700; margin: 0; display: flex; align-items: center; gap: 0.45rem; }
.oa-seccion__head small { color: var(--tinta-suave); }
.oa-seccion__head--fila { display: flex; justify-content: space-between; align-items: flex-start; gap: 0.75rem; flex-wrap: wrap; }
.oa-select { width: auto; min-width: 11rem; }
.oa-dos { display: grid; grid-template-columns: 1fr; max-width: 1200px; margin: 0 auto; }
@media (min-width: 992px) {
  .oa-dos { grid-template-columns: 1fr 1fr; gap: 1rem; }
  .oa-dos > .oa-seccion { margin-left: 0; margin-right: 0; }
}
@media (min-width: 992px) and (max-width: 1231px) { .oa-dos { padding: 0 0.75rem; } }

/* Descubrimientos */
.oa-insights { display: grid; gap: 0.65rem; grid-template-columns: repeat(auto-fill, minmax(250px, 1fr)); }
.oa-insight {
  display: flex;
  gap: 0.7rem;
  text-align: left;
  padding: 0.8rem 0.9rem;
  border-radius: 12px;
  border: 1px solid var(--linea);
  background: var(--papel);
  color: var(--tinta);
  animation: oa-entra 0.45s both;
  transition: transform 0.15s, box-shadow 0.15s;
}
.oa-insight:hover { transform: translateY(-2px); box-shadow: 0 6px 16px rgba(13, 27, 22, 0.08); }
.oa-insight__icono { font-size: 1.3rem; line-height: 1; margin-top: 0.1rem; }
.oa-insight__titulo { font-weight: 700; font-size: 0.92rem; }
.oa-insight__texto { font-size: 0.85rem; color: var(--tinta-suave); }
.oa-insight--mejora .oa-insight__icono, .oa-insight--logro .oa-insight__icono { color: var(--oa-mejor); }
.oa-insight--alerta .oa-insight__icono { color: var(--oa-peor); }
.oa-insight--patron .oa-insight__icono { color: #4f5bd5; }
.oa-insight--info .oa-insight__icono { color: var(--tinta-suave); }
.oa-insight--logro { background: linear-gradient(135deg, rgba(31, 107, 69, 0.08), var(--papel)); }
@keyframes oa-entra { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: none; } }

/* Tiles */
.oa-grupo + .oa-grupo { margin-top: 0.9rem; }
.oa-grupo__titulo { font-size: 0.72rem; text-transform: uppercase; letter-spacing: 0.1em; color: var(--tinta-suave); margin-bottom: 0.45rem; }
.oa-tiles { display: grid; gap: 0.6rem; grid-template-columns: repeat(auto-fill, minmax(150px, 1fr)); }
.oa-tile {
  position: relative;
  text-align: left;
  border: 1px solid var(--linea);
  background: var(--papel);
  border-radius: 12px;
  padding: 0.65rem 0.75rem 2.1rem;
  overflow: hidden;
  color: var(--tinta);
  transition: border-color 0.15s, box-shadow 0.15s;
}
.oa-tile:hover { border-color: rgba(22, 53, 40, 0.3); }
.oa-tile.is-sel { border-color: var(--marca); box-shadow: 0 0 0 2px var(--marca-suave); }
.oa-tile__label { font-size: 0.78rem; color: var(--tinta-suave); }
.oa-tile__valor { font-size: 1.35rem; line-height: 1.2; }
.oa-tile__pie { font-size: 0.78rem; }
.oa-spark { position: absolute; left: 0; right: 0; bottom: 0.25rem; width: 100%; height: 26px; }
.oa-spark__actual { fill: none; stroke-width: 1.6; vector-effect: non-scaling-stroke; }
.oa-spark__previo { fill: none; stroke: #b9c0bb; stroke-width: 1.2; stroke-dasharray: 3 3; vector-effect: non-scaling-stroke; }
.oa-delta { display: inline-flex; align-items: center; gap: 0.1rem; font-weight: 600; }
.oa-delta--mejor { color: var(--oa-mejor); }
.oa-delta--peor { color: var(--oa-peor); }
.oa-delta--igual { color: var(--tinta-suave); font-weight: 500; }

/* Duelo */
.oa-duelo-leyenda { display: flex; flex-wrap: wrap; gap: 0.4rem 1.1rem; font-size: 0.82rem; color: var(--tinta-suave); margin-bottom: 0.5rem; align-items: center; }
.oa-linea { display: inline-block; width: 22px; height: 2px; border-radius: 2px; vertical-align: middle; margin-right: 0.35rem; background: var(--tinta); }
.oa-linea--dash { background: repeating-linear-gradient(90deg, #9aa39d 0 5px, transparent 5px 9px); }
.oa-linea--gruesa { height: 6px; background: #4f5bd5; }
.oa-chart { height: 280px; }
.oa-resaltado-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.2rem 0.3rem 0.2rem 0.6rem;
  border-radius: 999px;
  background: rgba(180, 35, 24, 0.08);
  color: #8f1d14;
  font-size: 0.8rem;
  font-weight: 600;
}
.oa-resaltado-chip button { border: 0; background: transparent; color: inherit; padding: 0 0.2rem; }

/* Mapa de calor */
.oa-heat-wrap { display: flex; gap: 0.35rem; overflow-x: auto; padding: 4px 2px 6px; }
.oa-heat-dias { display: grid; grid-template-rows: repeat(7, var(--cell, 15px)); gap: 3px; font-size: 0.65rem; color: var(--tinta-suave); line-height: 1; }
.oa-heat-dias span { display: flex; align-items: center; height: var(--cell, 15px); }
.oa-heat { display: grid; grid-auto-flow: column; grid-template-rows: repeat(7, var(--cell)); grid-auto-columns: var(--cell); gap: 3px; }
.oa-heat__celda {
  width: var(--cell);
  height: var(--cell);
  border: 0;
  padding: 0;
  border-radius: 4px;
  transition: transform 0.12s;
}
.oa-heat__celda:not(.is-vacia):hover { transform: scale(1.25); z-index: 1; }
.oa-heat__celda.is-vacia { cursor: default; }
.oa-heat__celda.is-sel { outline: 2px solid var(--tinta); outline-offset: 1px; }
.oa-heat__celda.is-hl { box-shadow: 0 0 0 2px var(--superficie), 0 0 0 4px #b42318; animation: oa-latido 1.6s ease-in-out infinite; }
@keyframes oa-latido { 50% { box-shadow: 0 0 0 2px var(--superficie), 0 0 0 4px rgba(180, 35, 24, 0.35); } }
.oa-heat-escala { display: flex; align-items: center; gap: 3px; font-size: 0.72rem; color: var(--tinta-suave); margin-top: 0.4rem; }
.oa-heat-escala__paso { width: 14px; height: 10px; border-radius: 3px; }
.oa-heat-escala span:first-child { margin-right: 0.25rem; }
.oa-heat-escala span:last-child { margin-left: 0.25rem; }

.oa-dia { margin-top: 0.9rem; padding: 0.85rem; border-radius: 12px; background: var(--papel); border: 1px solid var(--linea); }
.oa-dia__head { display: flex; justify-content: space-between; align-items: center; margin-bottom: 0.5rem; text-transform: capitalize; }
.oa-dia__grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(130px, 1fr)); gap: 0.35rem 0.8rem; font-size: 0.82rem; }
.oa-dia__grid div { display: flex; justify-content: space-between; border-bottom: 1px dashed var(--linea); padding-bottom: 0.2rem; }
.oa-dia__grid span { color: var(--tinta-suave); }
.oa-dia__entrenos { margin-top: 0.5rem; display: flex; flex-wrap: wrap; gap: 0.3rem; }
.oa-pill { font-size: 0.75rem; padding: 0.15rem 0.5rem; border-radius: 999px; background: rgba(217, 98, 43, 0.1); color: #9a3d12; text-transform: capitalize; }

/* Reloj del sueño */
.oa-reloj { display: flex; align-items: center; gap: 1rem; flex-wrap: wrap; justify-content: center; }
.oa-reloj__svg { width: 240px; height: 240px; overflow: visible; }
.oa-reloj__esfera { fill: #f1f3fb; stroke: rgba(79, 91, 213, 0.15); }
.oa-reloj__marca { stroke: rgba(28, 36, 28, 0.25); stroke-width: 1; }
.oa-reloj__hora { font-size: 9px; fill: var(--tinta-suave); text-anchor: middle; }
.oa-reloj__arco { fill: none; stroke: #4f5bd5; stroke-width: 12; stroke-linecap: round; }
.oa-reloj__arco-previo { fill: none; stroke: #9aa39d; stroke-width: 3; stroke-dasharray: 5 4; stroke-linecap: round; }
.oa-reloj__punto { fill: #4f5bd5; opacity: 0.45; stroke: #f1f3fb; stroke-width: 1; }
.oa-reloj__punto.is-hl { fill: #b42318; opacity: 1; }
.oa-reloj__centro { font-size: 20px; text-anchor: middle; fill: var(--tinta); }
.oa-reloj__centro-sub { font-size: 10px; text-anchor: middle; fill: var(--tinta-suave); }
.oa-reloj__leyenda { list-style: none; padding: 0; margin: 0; font-size: 0.82rem; color: var(--tinta-suave); }
.oa-reloj__leyenda li { margin-bottom: 0.35rem; }

.oa-arquitectura { margin-top: 1rem; }
.oa-arquitectura__titulo { font-size: 0.82rem; font-weight: 700; margin-bottom: 0.4rem; }
.oa-arq-fila { display: flex; align-items: center; gap: 0.6rem; margin-bottom: 0.35rem; }
.oa-arq-fila__label { width: 3.2rem; font-size: 0.78rem; color: var(--tinta-suave); }
.oa-arq-barra { flex: 1; display: flex; gap: 2px; height: 22px; }
.oa-arq-seg {
  flex-basis: 0;
  min-width: 2px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.72rem;
  color: #fff;
  transition: flex-grow 0.6s ease;
}
.oa-arq-seg:first-child { border-radius: 4px 0 0 4px; }
.oa-arq-seg:last-child { border-radius: 0 4px 4px 0; }
.oa-arq-seg--deepMin { background: #26307f; }
.oa-arq-seg--remMin { background: #4f5bd5; }
.oa-arq-seg--lightMin { background: #a3aaf0; color: #1c241c; }
.oa-arq-leyenda { display: flex; gap: 0.9rem; font-size: 0.75rem; color: var(--tinta-suave); margin-top: 0.3rem; }
.oa-arq-leyenda i { display: inline-block; width: 10px; height: 10px; border-radius: 2px; margin-right: 0.3rem; vertical-align: -1px; }

/* Huella semanal */
.oa-semana__fila { display: grid; grid-template-columns: 2.4rem 1fr 4.6rem; align-items: center; gap: 0.5rem; margin-bottom: 0.45rem; }
.oa-semana__dia { font-size: 0.8rem; color: var(--tinta-suave); text-transform: capitalize; }
.oa-semana__fila.is-top .oa-semana__dia { color: var(--tinta); font-weight: 700; }
.oa-semana__barras { display: flex; flex-direction: column; gap: 2px; }
.oa-semana__barra { height: 10px; border-radius: 0 4px 4px 0; transition: width 0.6s ease; }
.oa-semana__barra--previo { height: 4px; background: #b9c0bb; }
.oa-semana__valor { font-size: 0.82rem; text-align: right; }
.oa-semana__leyenda { display: flex; gap: 1rem; font-size: 0.75rem; color: var(--tinta-suave); margin-top: 0.3rem; }
.oa-semana__leyenda i { display: inline-block; width: 12px; height: 8px; border-radius: 2px; margin-right: 0.3rem; }
.oa-semana__leyenda i.is-previo { height: 4px; background: #b9c0bb; vertical-align: 2px; }

/* Extremos */
.oa-extremos { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 0.7rem; }
.oa-extremo {
  text-align: left;
  border-radius: 14px;
  padding: 0.9rem;
  border: 1px solid var(--linea);
  color: var(--tinta);
  transition: transform 0.15s;
}
.oa-extremo:hover { transform: translateY(-2px); }
.oa-extremo--mejor { background: linear-gradient(150deg, rgba(20, 137, 90, 0.14), var(--papel) 70%); }
.oa-extremo--peor { background: linear-gradient(150deg, rgba(79, 91, 213, 0.14), var(--papel) 70%); }
.oa-extremo__head { font-size: 0.75rem; text-transform: uppercase; letter-spacing: 0.08em; font-weight: 700; display: flex; gap: 0.35rem; align-items: center; }
.oa-extremo--mejor .oa-extremo__head i { color: #c58a00; }
.oa-extremo--peor .oa-extremo__head i { color: #4f5bd5; }
.oa-extremo__dia { text-transform: capitalize; font-size: 0.9rem; }
.oa-extremo__media { font-size: 2.2rem; line-height: 1.1; }
.oa-extremo__datos { display: grid; grid-template-columns: 1fr 1fr; gap: 0.15rem 0.6rem; font-size: 0.78rem; color: var(--tinta-suave); }
.oa-extremo__datos strong { color: var(--tinta); font-weight: 600; }
.oa-entrenos-resumen { margin-top: 0.8rem; font-size: 0.85rem; }
.oa-entrenos-resumen i { color: #d9622b; }

/* Estados */
.oa-skeleton { max-width: 1200px; margin: 0 auto; padding: 0 0.75rem; display: grid; gap: 1rem; }
.oa-skeleton__bloque {
  height: 140px;
  border-radius: 16px;
  background: linear-gradient(90deg, rgba(22, 53, 40, 0.05), rgba(22, 53, 40, 0.1), rgba(22, 53, 40, 0.05));
  background-size: 200% 100%;
  animation: oa-brillo 1.3s linear infinite;
}
@keyframes oa-brillo { to { background-position: -200% 0; } }
.oa-vacio { text-align: center; padding: 3rem 1rem; color: var(--tinta-suave); }
.oa-vacio i { font-size: 2.5rem; }

/* Coach */
.oa-orbe {
  position: fixed;
  right: 1.1rem;
  bottom: calc(var(--app-tabbar-offset, 0px) + 1rem);
  z-index: 1030;
  width: 58px;
  height: 58px;
  border-radius: 50%;
  border: 0;
  color: #fff;
  font-size: 1.4rem;
  background: conic-gradient(from 200deg, #26a86b, #7781f5, #d96a32, #26a86b);
  box-shadow: 0 10px 28px rgba(13, 27, 22, 0.35);
  transition: transform 0.2s;
}
.oa-orbe i { position: relative; z-index: 1; }
.oa-orbe::before { content: ''; position: absolute; inset: 4px; border-radius: 50%; background: var(--oa-night); }
.oa-orbe:hover { transform: scale(1.06); }
.oa-orbe__halo {
  position: absolute;
  inset: -6px;
  border-radius: 50%;
  border: 2px solid rgba(119, 129, 245, 0.5);
  animation: oa-halo 2.4s ease-out infinite;
}
.oa-orbe.is-open .oa-orbe__halo { display: none; }
@keyframes oa-halo { from { transform: scale(0.9); opacity: 1; } to { transform: scale(1.35); opacity: 0; } }

.oa-coach {
  position: fixed;
  right: 1rem;
  bottom: calc(var(--app-tabbar-offset, 0px) + 5.2rem);
  z-index: 1031;
  width: min(420px, calc(100vw - 2rem));
  height: min(600px, calc(100dvh - var(--app-tabbar-offset, 0px) - 8rem));
  display: flex;
  flex-direction: column;
  background: var(--superficie);
  border: 1px solid var(--linea);
  border-radius: 18px;
  box-shadow: 0 24px 60px rgba(13, 27, 22, 0.3);
  overflow: hidden;
}
.oa-coach__head {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  padding: 0.8rem 0.9rem;
  color: #eef4ef;
  background: linear-gradient(140deg, var(--oa-night), var(--oa-night-2));
}
.oa-coach__avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: grid;
  place-items: center;
  background: conic-gradient(from 200deg, #26a86b, #7781f5, #d96a32, #26a86b);
}
.oa-coach__nombre { font-weight: 700; }
.oa-coach__ctx { font-size: 0.75rem; color: #a9c7b6; }
.oa-coach__limpiar { color: #a9c7b6; }
.oa-coach__hilo { flex: 1; overflow-y: auto; padding: 0.9rem; display: flex; flex-direction: column; gap: 0.55rem; }
.oa-coach__intro p { font-size: 0.88rem; color: var(--tinta-suave); }
.oa-coach__sugerencias { display: flex; flex-direction: column; gap: 0.4rem; }
.oa-coach__sugerencias button {
  text-align: left;
  border: 1px solid var(--linea);
  background: var(--papel);
  border-radius: 12px;
  padding: 0.45rem 0.7rem;
  font-size: 0.85rem;
  color: var(--tinta);
}
.oa-coach__sugerencias button:hover:not(:disabled) { border-color: #7781f5; }
.oa-msg { display: flex; flex-direction: column; align-items: flex-start; }
.oa-msg--user { align-items: flex-end; }
.oa-msg__burbuja {
  max-width: 88%;
  padding: 0.55rem 0.8rem;
  border-radius: 14px;
  font-size: 0.88rem;
  background: var(--papel);
  border: 1px solid var(--linea);
}
.oa-msg--user .oa-msg__burbuja { background: var(--marca-oscura); color: #fff; border-color: transparent; border-bottom-right-radius: 4px; }
.oa-msg--assistant .oa-msg__burbuja { border-bottom-left-radius: 4px; }
.oa-msg__accion {
  margin-top: 0.3rem;
  border: 0;
  background: rgba(180, 35, 24, 0.08);
  color: #8f1d14;
  border-radius: 999px;
  font-size: 0.75rem;
  padding: 0.2rem 0.6rem;
}
.oa-msg__accion i { margin-right: 0.25rem; }
.oa-pensando { display: inline-flex; gap: 4px; }
.oa-pensando span { width: 7px; height: 7px; border-radius: 50%; background: #7781f5; animation: oa-bote 1s infinite ease-in-out; }
.oa-pensando span:nth-child(2) { animation-delay: 0.15s; }
.oa-pensando span:nth-child(3) { animation-delay: 0.3s; }
@keyframes oa-bote { 0%, 80%, 100% { transform: translateY(0); opacity: 0.5; } 40% { transform: translateY(-5px); opacity: 1; } }
.oa-coach__form { display: flex; gap: 0.45rem; padding: 0.7rem; border-top: 1px solid var(--linea); }

.oa-sube-enter-active, .oa-sube-leave-active { transition: opacity 0.2s, transform 0.25s cubic-bezier(0.2, 0.8, 0.2, 1); }
.oa-sube-enter-from, .oa-sube-leave-to { opacity: 0; transform: translateY(16px) scale(0.97); }
.oa-fade-enter-active, .oa-fade-leave-active { transition: opacity 0.2s; }
.oa-fade-enter-from, .oa-fade-leave-to { opacity: 0; }

@media (max-width: 575px) {
  .oa-chips { flex-wrap: nowrap; overflow-x: auto; scrollbar-width: none; margin: 0 -0.25rem; padding: 0 0.25rem; }
  .oa-chips::-webkit-scrollbar { display: none; }
  .oa-chip { flex: none; }
  .oa-filtros { gap: 0.5rem; padding: 0.6rem 0.75rem; }
  .oa-rango { margin-left: 0; font-size: 0.78rem; }
}

@media (prefers-reduced-motion: reduce) {
  .oa *, .oa *::before, .oa *::after { animation: none !important; transition: none !important; }
}
</style>
